import '@theme/product-form';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

let formNumber = 0;
let feedbackTimer;
const feedbackId = 'TrCardCartFeedback';

function ensureFeedback() {
  let feedback = document.getElementById(feedbackId);
  if (!feedback) {
    feedback = document.createElement('div');
    feedback.id = feedbackId;
    feedback.className = 'tr-cart-feedback';
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    feedback.setAttribute('aria-atomic', 'true');
    document.body.append(feedback);
  }
  return feedback;
}

function showFeedback(message, state, sourceDialog) {
  const feedback = ensureFeedback();
  // A live region outside a modal is inert. Keep feedback in the active dialog.
  const host = sourceDialog?.open ? sourceDialog : document.querySelector('dialog:modal') ?? document.body;
  if (feedback.parentElement !== host) host.append(feedback);
  clearTimeout(feedbackTimer);
  feedback.dataset.state = state;
  feedback.dataset.visible = 'true';
  feedback.textContent = '';
  requestAnimationFrame(() => { feedback.textContent = message; });
  if (state !== 'loading') {
    feedbackTimer = setTimeout(() => {
      feedback.dataset.visible = 'false';
      feedback.textContent = '';
    }, state === 'error' ? 10000 : 3000);
  }
}

class CardQuickAdd extends Component {
  #listeners;
  #formListeners;
  #pending = false;
  #request;
  #completionObserved = false;
  #errorMessage = '';
  #resetTimer;
  #sourceDialog;
  #productForm;
  #returnFocus = false;
  #activeProduct;
  #visibleProduct;
  #state = 'idle';
  #stateObserver = new MutationObserver(() => this.#synchronize());

  #identity() {
    return `${this.dataset.productId}:${this.dataset.variantId}:${this.dataset.directAdd}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.addEventListener('click', this.#submit, { signal: this.#listeners.signal });
    this.#visibleProduct = this.#identity();
    this.#setState(this.#pending ? 'loading' : 'idle');
    this.#stateObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-product-id', 'data-variant-id', 'data-direct-add', 'data-state'],
    });
    ensureFeedback();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.isConnected) this.#synchronize();
  }

  #synchronize() {
    const identity = this.#identity();
    if (this.#pending) this.#setState(this.querySelector('button') ? 'loading' : 'idle');
    else if (this.#visibleProduct !== identity) {
      this.#removeForm();
      clearTimeout(this.#resetTimer);
      this.#setState('idle');
    } else {
      this.#setState(this.#state);
    }
    this.#visibleProduct = identity;
  }

  #prepareForm(button) {
    this.#removeForm();
    const template = this.querySelector('template[data-card-product-form]');
    if (!template) return null;
    const productForm = template.content.querySelector('product-form-component')?.cloneNode(true);
    if (!(productForm instanceof HTMLElement)) return null;
    this.#productForm = productForm;
    productForm.id = `TrCardForm-${++formNumber}`;
    const form = productForm.querySelector('form');
    if (!form) return null;
    // Horizon can retain an inert template's old content while morphing its owner.
    // The live server-rendered attributes remain the authoritative current IDs.
    productForm.dataset.productId = this.dataset.productId;
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput && this.dataset.variantId) variantInput.value = this.dataset.variantId;
    const formId = `${productForm.id}-form`;
    form.setAttribute('id', formId);
    button.setAttribute('form', formId);
    productForm.style.display = 'none';
    // Keep valid HTML when the card is inside the predictive-search form.
    const containingForm = this.closest('form');
    (containingForm?.parentElement ?? this).append(productForm);
    this.#formListeners = new AbortController();
    const { signal } = this.#formListeners;
    form?.addEventListener('submit', this.#submit, { signal });
    productForm.addEventListener(StandardEvents.cartLinesUpdate, this.#cartUpdate, { signal });
    productForm.addEventListener(StandardEvents.cartError, this.#cartError, { signal });
    return productForm;
  }

  #removeForm() {
    this.#formListeners?.abort();
    this.#productForm?.remove();
    this.#productForm = null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stateObserver.disconnect();
    this.#listeners?.abort();
    clearTimeout(this.#resetTimer);
    this.#removeForm();
  }

  #setState(state) {
    this.#state = state;
    if (this.dataset.state !== state) this.dataset.state = state;
    const button = this.querySelector('button');
    if (!button) return;
    button.disabled = state === 'loading';
    if (state === 'loading') button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  #submit = (event) => {
    const button = event.type === 'submit'
      ? this.querySelector('button.tr-quick-add__button')
      : event.target.closest('button.tr-quick-add__button');
    if (event.type !== 'submit' && (!button || !this.contains(button))) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#pending || !button || this.dataset.directAdd === 'false') return;
    // Read the current server-rendered template on intent, not a stale search result.
    const productForm = this.#prepareForm(button);
    if (typeof productForm?.handleSubmit !== 'function') {
      showFeedback(this.dataset.errorMessage, 'error', this.closest('dialog[open]'));
      return;
    }
    clearTimeout(this.#resetTimer);
    this.#request = Symbol();
    this.#activeProduct = this.#identity();
    this.#pending = true;
    this.#completionObserved = false;
    this.#errorMessage = '';
    this.#sourceDialog = this.closest('dialog[open]');
    this.#returnFocus = document.activeElement === this.querySelector('button');
    this.querySelector('button')?.removeAttribute('aria-describedby');
    this.#setState('loading');
    showFeedback(this.dataset.loadingMessage, 'loading', this.#sourceDialog);
    try {
      // Reuse Horizon's add request, bundled sections and standardized cart events.
      // There is deliberately no speculative add/checkmark animation here.
      productForm.handleSubmit(event);
    } catch {
      this.#finish(false, this.dataset.errorMessage);
    }
  };

  #cartUpdate = (event) => {
    if (event.action !== 'add' || event.target !== this.#productForm || !this.#pending) return;
    const request = this.#request;
    this.#completionObserved = true;
    event.promise
      .then(({ detail }) => {
        if (request !== this.#request) return;
        return this.#finish(!detail?.didError, this.#errorMessage || this.dataset.errorMessage);
      })
      .catch(() => {
        if (request === this.#request) this.#finish(false, this.#errorMessage || this.dataset.errorMessage);
      });
  };

  #cartError = (event) => {
    if (event.target !== this.#productForm || !this.#pending) return;
    const description = event.detail?.description;
    this.#errorMessage = event.code === 'INVALID'
      ? (typeof description === 'string' && description.trim() ? description : event.error)
      : this.dataset.errorMessage;
    if (!this.#completionObserved) this.#finish(false, this.#errorMessage);
  };

  async #finish(success, errorMessage) {
    const request = this.#request;
    const sameProduct = this.#activeProduct === this.#identity() && Boolean(this.querySelector('button'));
    this.#pending = false;
    this.#setState(sameProduct ? (success ? 'success' : 'error') : 'idle');
    if (sameProduct && this.#returnFocus && this.isConnected && document.activeElement === document.body) {
      this.querySelector('button')?.focus({ preventScroll: true });
    }
    if (!success) {
      if (sameProduct) this.querySelector('button')?.setAttribute('aria-describedby', feedbackId);
      showFeedback(errorMessage || this.dataset.errorMessage, 'error', this.#sourceDialog);
      return;
    }
    if (this.isConnected) {
      this.#resetTimer = setTimeout(() => this.#setState('idle'), 1500);
    }
    const sourceDialog = this.#sourceDialog;
    if (sourceDialog?.open) {
      const owner = sourceDialog.closest('dialog-component');
      try {
        if (typeof owner?.closeDialog === 'function') await owner.closeDialog();
        else sourceDialog.close();
      } catch {
        if (sourceDialog.open) sourceDialog.close();
      }
    }
    if (request !== this.#request) return;
    showFeedback(this.dataset.successMessage, 'success');
  }
}

if (!customElements.get('card-quick-add')) customElements.define('card-quick-add', CardQuickAdd);

export { showFeedback as showCartFeedback };
